/**
 * A still frame and a length, read off a video the sender picked.
 *
 * The server cannot do either: it has no decoder, and putting one there would
 * mean ffmpeg beside sharp for the one thing a browser already has open in front
 * of it. So a video going into an album is read here, exactly as a кружочек's
 * poster is grabbed from the canvas it was encoded through (see capture.ts) —
 * the difference being that this file was not made by us and may be anything.
 *
 * Which is why every step of it is allowed to fail: a codec the browser will not
 * decode, a file whose metadata never loads, a seek that never lands. A video
 * with no frame still sends; its tile draws a placeholder with the play badge on
 * it, and the length is simply missing.
 *
 * **There are two ways to read one, and the fast one is tried first.**
 *
 * The obvious way is a `<video>` element: point it at the file, seek, draw the
 * element onto a canvas. It works everywhere and it is what this file did for
 * every video until now — but what it costs is set by the size of the file
 * rather than by the frame. A phone writes its `moov` atom at the *end* of the
 * MP4, so `loadedmetadata` cannot fire until the element has read the whole
 * thing: a minute of 4K off an Honor is 200 МБ through the WebView's content
 * bridge before the first pixel is available. That is the several seconds the
 * compose screen used to open into.
 *
 * The fast way is the demuxer this app already carries for compression (see
 * videoCompress.ts): mediabunny reads the container over ranged slices of the
 * Blob — the tail for the index, then one group of pictures around the moment
 * being asked for — and WebCodecs decodes those few frames. Some megabytes
 * instead of all of them, and no element, no seek, nothing waiting on events.
 * The chunk is loaded on demand and precached by the service worker, so
 * reaching for it costs a moment on the first video of a session.
 *
 * The element stays as the fallback and is not going anywhere: Firefox and
 * Safari before 16.4 have no `VideoDecoder`, and a container mediabunny cannot
 * parse may still open in the browser that shipped with it.
 *
 * **The two ways a poster comes out black**, both of which this file is arranged
 * around, because a black tile is worse than no tile: it looks like a picture of
 * nothing rather than like a video waiting to be played.
 *
 * The first is drawing too early. `seeked` says the seek finished, not that the
 * frame at the new position has been decoded — the element may still be at
 * `HAVE_METADATA`, and `drawImage` from an element with no current frame draws
 * nothing at all, silently, leaving the canvas exactly as it was. So the wait is
 * for a frame to be *presented* (`requestVideoFrameCallback`, which exists for
 * this), with `readyState` as the fallback where it does not exist. The decoder
 * path has no version of this problem: a decoded frame either exists or the call
 * handed back nothing.
 *
 * The second is that the frame really is black. A phone camera opens its
 * aperture over the first few tenths of a second, and a clip that starts indoors
 * or at night is genuinely dark until it is not. One timestamp cannot dodge
 * that, so several are tried in turn and the first one with any light in it
 * wins — on both paths, from the same list.
 */

import type { Input as MediaInput } from 'mediabunny';

/** Where the frame is looked for, in order, in seconds.
    Not zero — the first frame of a phone video is very often black. The later
    two are for when a tenth of a second in is black as well. */
const FRAME_AT_S = [0.1, 1, 3];

/** How long to wait for a browser to decode enough to answer. */
const TIMEOUT_MS = 4000;

/**
 * The point at which reading a file is abandoned, whichever path is on.
 *
 * A backstop rather than a budget, and deliberately above the element path's own
 * arithmetic — four seconds for the metadata and eight for each of three
 * moments, so twenty-eight at its very worst. Cutting in under that would be
 * this timer overruling the ones that know what they are waiting for, and the
 * result would be no frame *and* no length from a read that was going to produce
 * both.
 *
 * What it exists for is the step that has no limit of its own: a decode that
 * simply never returns. The compose screen holds its send button until every
 * picked video has been read, and a clip being read forever is a screen that can
 * only be cancelled.
 */
const DEADLINE_MS = 30_000;

/** The frame is a tile, at most 232px across; there is nothing to gain past it. */
const FRAME_BOX = 480;

/** `HTMLMediaElement.HAVE_CURRENT_DATA` — the readyState at which there is a
    frame at the current position, which is the one `drawImage` needs. */
const HAVE_CURRENT_DATA = 2;

export interface VideoFrame {
  /** A JPEG of the frame, or `null` if the browser would not give one up. */
  poster: Blob | null;
  /** How long the clip runs, or `null` if it never said. */
  durationMs: number | null;
}

const NOTHING: VideoFrame = { poster: null, durationMs: null };

/** Resolves with whichever comes first: the event, or giving up. */
function firstOf(element: HTMLVideoElement, event: string): Promise<boolean> {
  return new Promise((resolve) => {
    const timer = window.setTimeout(() => finish(false), TIMEOUT_MS);
    const finish = (ok: boolean) => {
      window.clearTimeout(timer);
      element.removeEventListener(event, onEvent);
      element.removeEventListener('error', onError);
      resolve(ok);
    };
    const onEvent = () => finish(true);
    const onError = () => finish(false);
    element.addEventListener(event, onEvent);
    element.addEventListener('error', onError);
  });
}

/**
 * Resolves once a frame has actually been handed to the compositor.
 *
 * Must be called *before* the seek that is being waited on: a callback
 * registered after the browser has already presented the frame would sit there
 * waiting for the next one, which on a paused element never comes.
 *
 * Never rejects and never hangs — a browser without the callback, or one that
 * simply does not fire it, ends at the timeout and leaves the decision to
 * `readyState`.
 */
function framePresented(video: HTMLVideoElement): Promise<void> {
  return new Promise((resolve) => {
    let settled = false;
    const finish = () => {
      if (settled) return;
      settled = true;
      window.clearTimeout(timer);
      resolve();
    };
    const timer = window.setTimeout(finish, TIMEOUT_MS);

    if (typeof video.requestVideoFrameCallback === 'function') {
      video.requestVideoFrameCallback(() => finish());
      return;
    }
    // Firefox, and Safari before 15.4. Polling readyState is weaker — it says a
    // frame exists rather than that it has been drawn — but it is the only
    // signal those browsers have, and it is still stronger than `seeked`.
    const poll = () => {
      if (settled) return;
      if (video.readyState >= HAVE_CURRENT_DATA) finish();
      else window.requestAnimationFrame(poll);
    };
    poll();
  });
}

/** Puts the element on `seconds` and waits until there is something to draw. */
async function frameAt(video: HTMLVideoElement, seconds: number): Promise<boolean> {
  const presented = framePresented(video);
  video.currentTime = seconds;
  if (!(await firstOf(video, 'seeked'))) return false;
  await presented;
  return video.readyState >= HAVE_CURRENT_DATA && video.videoWidth > 0;
}

/**
 * The timestamps to try, for a clip this long — in seconds, `null` when the
 * container never recorded a length.
 *
 * Only moments the clip actually contains: seeking past the end lands nowhere,
 * and a two-second clip asked for its third second answers with whatever it was
 * already showing. A clip shorter than the first candidate gets its own middle,
 * which is the one moment every clip has.
 */
export function frameMoments(durationSeconds: number | null): number[] {
  if (durationSeconds === null || !Number.isFinite(durationSeconds)) return [...FRAME_AT_S];
  if (!(durationSeconds > 0)) return [0];
  const inside = FRAME_AT_S.filter((at) => at < durationSeconds);
  return inside.length > 0 ? inside : [durationSeconds / 2];
}

/** Every this-many-th pixel is looked at. A tile-sized frame is ~150k pixels
    and the question — is there any light here at all — does not need them all. */
const BLANK_STRIDE = 7;

/** Above this on any one pixel and the frame is a picture. Deliberately low:
    this is asking whether the decoder drew anything, not whether the shot is
    well exposed, and a dark room still has a highlight somewhere in it. */
const BLANK_LUMA = 12;

/**
 * True when there is no light anywhere in these pixels — an undrawn canvas, or
 * a frame the camera caught before its aperture opened.
 *
 * Brightest pixel rather than average, because the average is a question about
 * the exposure and this is a question about whether there is an image at all: a
 * night shot averages near zero and still has a streetlight in it.
 */
export function isBlankFrame(pixels: Uint8ClampedArray): boolean {
  for (let at = 0; at < pixels.length; at += 4 * BLANK_STRIDE) {
    // Rec. 601 luma, integer-weighted; the exact coefficients do not matter at
    // this threshold, only that green counts for most of it.
    const luma = (pixels[at] * 77 + pixels[at + 1] * 150 + pixels[at + 2] * 29) >> 8;
    if (luma > BLANK_LUMA) return false;
  }
  return true;
}

/**
 * The box a frame this shape is drawn into: inside FRAME_BOX, aspect ratio kept,
 * never scaled up. Shared by both paths, so which one read the clip is not
 * something a tile can tell.
 */
export function frameBox(width: number, height: number): { width: number; height: number } {
  const scale = Math.min(1, FRAME_BOX / Math.max(width, height));
  return {
    width: Math.max(1, Math.round(width * scale)),
    height: Math.max(1, Math.round(height * scale)),
  };
}

/** The canvas as a JPEG, or `null` — including for a canvas the browser has
    tainted and will not read back, which throws here rather than resolving. */
function toJpeg(canvas: HTMLCanvasElement): Promise<Blob | null> {
  return new Promise((resolve) => {
    try {
      canvas.toBlob((blob) => resolve(blob), 'image/jpeg', 0.82);
    } catch {
      resolve(null);
    }
  });
}

/** True when the pixels on this canvas have no light in them — and false when
    the browser will not let them be read, since a frame we cannot look at is not
    a frame we can call blank. */
function isBlankCanvas(context: CanvasRenderingContext2D, canvas: HTMLCanvasElement): boolean {
  try {
    return isBlankFrame(context.getImageData(0, 0, canvas.width, canvas.height).data);
  } catch {
    return false;
  }
}

/**
 * The fast path: the container read over slices of the file, the frame decoded
 * by WebCodecs.
 *
 * `null` means «not this one» — no decoder in this browser, a container
 * mediabunny will not open, a codec it cannot hand to WebCodecs, a decode that
 * produced nothing — and the element path takes over. A length without a frame
 * is given up along with it rather than kept: the element is about to read the
 * length for itself anyway, and it may find the frame this did not.
 */
async function frameByDecoder(file: File): Promise<VideoFrame | null> {
  if (typeof document === 'undefined' || !('VideoDecoder' in globalThis)) return null;

  let input: MediaInput | null = null;
  try {
    const { ALL_FORMATS, BlobSource, CanvasSink, Input } = await import('mediabunny');

    input = new Input({ formats: ALL_FORMATS, source: new BlobSource(file) });
    const track = await input.getPrimaryVideoTrack();
    // Asked before a decoder is configured, so «this browser has no HEVC» costs
    // a question rather than a failed decode.
    if (!track || !(await track.canDecode())) return null;

    const seconds = await input.computeDuration();
    const finite = Number.isFinite(seconds) && seconds > 0;

    if (track.displayWidth === 0 || track.displayHeight === 0) return null;
    const box = frameBox(track.displayWidth, track.displayHeight);

    const canvas = document.createElement('canvas');
    canvas.width = box.width;
    canvas.height = box.height;
    // Without alpha, so the pixels the blank check reads are the ones the tile
    // would show; the JPEG below has no transparency to give back anyway.
    const context = canvas.getContext('2d', { alpha: false });
    if (!context) return null;

    const sink = new CanvasSink(track, {
      ...box,
      // The box came from this track's own display size, so there is nothing to
      // stretch — the same argument as in videoCompress.ts, and mediabunny
      // refuses a width and a height without a fit either way.
      fit: 'fill',
    });

    let drew = false;
    // One pipeline over all the candidates rather than a decode each:
    // mediabunny walks sorted timestamps decoding every packet at most once,
    // and because the iteration is lazy, a first frame with light in it costs
    // only the frames before it in its own group of pictures.
    for await (const wrapped of sink.canvasesAtTimestamps(frameMoments(finite ? seconds : null))) {
      // A moment before the track's first frame. The later candidates may still
      // land, so this is a skip rather than the end.
      if (!wrapped) continue;
      context.drawImage(wrapped.canvas, 0, 0, canvas.width, canvas.height);
      drew = true;
      if (!isBlankCanvas(context, canvas)) break;
    }
    if (!drew) return null;

    const poster = await toJpeg(canvas);
    if (!poster) return null;
    return { poster, durationMs: finite ? Math.round(seconds * 1000) : null };
  } catch (error) {
    console.warn('videoFrame: reading it with an element instead', error);
    return null;
  } finally {
    input?.dispose();
  }
}

/**
 * The fallback: a `<video>` element seeked and drawn onto a canvas.
 *
 * Slower by however large the file is (see the note at the top of this file),
 * and the only way to read a clip in a browser without WebCodecs — or one whose
 * container the demuxer above declined.
 */
async function frameByElement(file: File): Promise<VideoFrame> {
  if (typeof document === 'undefined') return NOTHING;
  const url = URL.createObjectURL(file);
  const video = document.createElement('video');

  try {
    // Everything about the element before its source, and the source last: the
    // browser reads `preload` and `muted` when the load it schedules actually
    // runs, and a muted element is the one a mobile browser will decode without
    // a user gesture.
    video.muted = true;
    video.playsInline = true;
    // `auto`, not `metadata`: this element exists to be seeked and drawn, and
    // asking for metadata alone leaves some browsers deciding for themselves how
    // much of the frame data to fetch for the seek that follows.
    video.preload = 'auto';
    video.setAttribute('aria-hidden', 'true');
    video.style.cssText =
      'position:fixed;width:1px;height:1px;opacity:0;pointer-events:none;left:-1px;top:-1px';
    // Attached, if invisibly: Safari will not decode a video element that is not
    // in the document, and an undecoded element draws nothing — the same reason
    // startVideoNote puts its capture element there.
    document.body.appendChild(video);
    video.src = url;
    // Asked for explicitly rather than left to the assignment above, which some
    // browsers defer — for a tab that is not on screen, indefinitely.
    video.load();

    if (!(await firstOf(video, 'loadedmetadata'))) return NOTHING;

    // A file whose length the container never recorded reports Infinity, which
    // is the same nothing `durationMs` means everywhere else in this app.
    const finite = Number.isFinite(video.duration);
    const duration = finite ? Math.round(video.duration * 1000) : null;

    const width = video.videoWidth;
    const height = video.videoHeight;
    if (width === 0 || height === 0) return { poster: null, durationMs: duration };

    const box = frameBox(width, height);
    const canvas = document.createElement('canvas');
    canvas.width = box.width;
    canvas.height = box.height;
    // Without alpha, so that a frame that failed to draw is opaque black rather
    // than transparent — the JPEG below would flatten it to black either way,
    // and this way the blank check below sees the same pixels the tile would.
    const context = canvas.getContext('2d', { alpha: false });
    if (!context) return { poster: null, durationMs: duration };

    let drew = false;
    for (const moment of frameMoments(finite ? video.duration : null)) {
      if (!(await frameAt(video, moment))) continue;
      // Cleared first: `drawImage` from an element with nothing decoded is a
      // no-op, and without this the previous attempt's pixels would answer the
      // blank check for it.
      context.clearRect(0, 0, canvas.width, canvas.height);
      context.drawImage(video, 0, 0, canvas.width, canvas.height);
      drew = true;
      if (!isBlankCanvas(context, canvas)) break;
    }
    // Every moment either failed to land or came back black. The last draw
    // stands: a clip that really is dark all the way through should show as
    // dark, and one whose frames never arrived has nothing better to offer.
    if (!drew) return { poster: null, durationMs: duration };

    return { poster: await toJpeg(canvas), durationMs: duration };
  } catch {
    return NOTHING;
  } finally {
    video.remove();
    video.removeAttribute('src');
    URL.revokeObjectURL(url);
  }
}

/**
 * The frame and the length, by whichever path this browser and this file allow.
 *
 * Never rejects: a video nothing here could read comes back as no frame and no
 * length, which every caller already has to handle.
 */
export async function readVideoFrame(file: File): Promise<VideoFrame> {
  const read = (async () => (await frameByDecoder(file)) ?? (await frameByElement(file)))();
  // Whichever answers first, and the deadline answers nothing — see its comment.
  // Nothing is cancelled: an abandoned read finishes into a result no one is
  // holding and is collected along with it.
  return Promise.race([read, deadline()]);
}

function deadline(): Promise<VideoFrame> {
  return new Promise((resolve) => {
    setTimeout(() => resolve(NOTHING), DEADLINE_MS);
  });
}
